import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { createEffect, ofType, Actions } from "@ngrx/effects";
import { RouterRequestAction, routerRequestAction, RouterRequestPayload, SerializedRouterStateSnapshot } from "@ngrx/router-store";
import { Store } from "@ngrx/store";
import { map, filter, tap, pipe, Observable, catchError, switchMap, of } from "rxjs";
import { AppRoute } from "src/app/app-routing.module";
import { QualificationOutcome } from "src/app/core/enums/lead/qualification-outcome.enum";
import { isAuthenticated } from "src/app/shared/authentication/authentication.selectors";
import { AgentAction } from "../agent.actions";
import { AgentQualificationOutcomes } from "../models/agent-qualification-outcome.type";
import { LobbyError } from "src/app/core/models/common/lobby-error.model";
import { LobbyErrorCode } from "src/app/infrastructure/lobby-proxy/enums/lobby-error-code.enum";
import { LobbyErrorHandlerService } from "src/app/core/services/lobby-error-handler.service";

// /lead/cfdada87-0436-48b2-8ce8-31eb598aeccc.qualify?outcome=interested

const IsAgentRouteActionUrl = pipe(
    filter((action: RouterRequestAction) => {
        const urlFragments = action.payload.event.url.split('/')
        return urlFragments[1] == 'lead' && urlFragments.length > 2
    })
)

const IsOfferAcceptRouteActionUrl = pipe(
    filter((action: RouterRequestAction) => {
        return action.payload.event.url.includes('.acceptoffer')
    })
)

const IsOfferRefuseRouteActionUrl = pipe(
    filter((action: RouterRequestAction) => {
        return action.payload.event.url.includes('.refuseoffer')
    })
)

const IsQualifyRouteActionUrl = pipe(
    filter((action: RouterRequestAction) => {
        return action.payload.event.url.includes('.qualify')
    })
)

const mapQualificationRequest = pipe(
    map((action: {
        payload: RouterRequestPayload<SerializedRouterStateSnapshot>;
    }) => {

        const actionFragment = action.payload.event.url.split('/')[2]
        const id = actionFragment.substring(0, actionFragment.indexOf('.'))
        const outcome = actionFragment.substring(actionFragment.indexOf('outcome=') + 'outcome='.length)

        let qualificationOutcome: QualificationOutcome

        switch (outcome?.toLowerCase()) {
            case 'interested': qualificationOutcome = QualificationOutcome.Interested; break
            case 'neverreplied': qualificationOutcome = QualificationOutcome.NeverReplied; break
            case 'noninterested': qualificationOutcome = QualificationOutcome.NonInterested; break
            case 'spam': qualificationOutcome = QualificationOutcome.Spam; break
            case 'unfitting': qualificationOutcome = QualificationOutcome.Unfitting; break

            default: throw new LobbyError(LobbyErrorCode.QualificationOutcomeInvalid, 'action link', `Invalid action parsing, ${outcome} is not valid`)
        }

        return [id, qualificationOutcome] as [string | number, AgentQualificationOutcomes]
    })
)

const mapLeadId = pipe(
    map((action: {
        payload: RouterRequestPayload<SerializedRouterStateSnapshot>;
    }) => {
        const actionFragment = action.payload.event.url.split('/')[2]
        return actionFragment.substring(0, actionFragment.indexOf('.'))
    })
)



@Injectable()
export class AgentRouteActionsEffects {

    readonly acceptOffer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(routerRequestAction),
            IsAgentRouteActionUrl,
            IsOfferAcceptRouteActionUrl,
            mapLeadId,
            switchMap((id) => this.isUserAuthenticated$.pipe(
                filter(isLogged => isLogged),
                tap(() => this.router.navigate([AppRoute.agent])),
                map(() => AgentAction.acceptOffer(id))
            ))
        )
    )

    readonly refuseOffer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(routerRequestAction),
            IsAgentRouteActionUrl,
            IsOfferRefuseRouteActionUrl,
            mapLeadId,
            switchMap((id) => this.isUserAuthenticated$.pipe(
                filter(isLogged => isLogged),
                tap(() => this.router.navigate([AppRoute.agent])),
                map(() => AgentAction.refuseOffer(id))
            ))
        )
    )

    readonly qualification$ = createEffect(() =>
        this.actions$.pipe(
            ofType(routerRequestAction),
            IsAgentRouteActionUrl,
            IsQualifyRouteActionUrl,
            mapQualificationRequest,
            this.lobbyErrorHandlerService.handleError,
            switchMap((request) => this.isUserAuthenticated$.pipe(
                filter(isLogged => isLogged),
                tap(() => this.router.navigate([AppRoute.agent])),
                map(() => AgentAction.qualifyLead(request))
            ))
        )
    )

    protected isUserAuthenticated$: Observable<boolean>

    constructor(
        private readonly appStore: Store,
        private readonly actions$: Actions,
        private readonly router: Router,
        private readonly lobbyErrorHandlerService: LobbyErrorHandlerService
    ) {
        this.isUserAuthenticated$ = appStore.pipe(isAuthenticated)
    }




}